import { Button } from "@/components/ui/button";
import { CardWrapper } from "@/components/card-wrapper";
import { Mail } from "lucide-react";
import { RequestSource } from "@/lib/errorHandling";
import { toast } from "sonner";
import { useHandleError } from "@/hooks/useHandleError";
import { useLocation, useNavigate } from "react-router";
import { usePocketBase } from "@/context/pocketbase-context";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function EmailConfirmationPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { handleError } = useHandleError();
  const { emailConfirmation } = usePocketBase();
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string })?.email ?? "";
  const [isResending, setIsResending] = useState(false);

  const handleResendEmail = async () => {
    if (!email) return;
    setIsResending(true);
    try {
      await emailConfirmation(email);
      toast.success(t("pages:emailConfirmation.resendSuccess"));
    } catch (error) {
      toast.error(handleError(RequestSource.SIGNUP, error));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="pt-12 flex justify-center items-center w-screen h-screen p-4">
      <CardWrapper className="w-[380px] md:w-[450px]">
        <div className="space-y-6 p-4 px-8">
          <div className="flex justify-center">
            <div className="rounded-full bg-primary/10 p-4">
              <Mail className="h-10 w-10 text-primary" />
            </div>
          </div>
          <div className="space-y-2 text-center">
            <h1 className="text-3xl font-bold">
              {t("pages:emailConfirmation.title")}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {t("pages:emailConfirmation.subtitle")}
            </p>
          </div>
          {email && (
            <p className="text-center text-sm font-medium">{email}</p>
          )}
          <p className="text-center text-sm text-muted-foreground">
            {t("pages:emailConfirmation.instruction")}
          </p>
          {email && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {t("pages:emailConfirmation.noEmail")}{" "}
                <button
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleResendEmail}
                  disabled={isResending}
                  className="font-medium text-primary hover:text-primary/90 underline disabled:opacity-50"
                >
                  {isResending
                    ? t("pages:emailConfirmation.resending")
                    : t("pages:emailConfirmation.resend")}
                </button>
              </p>
            </div>
          )}
          <Button className="w-full" onClick={() => navigate("/login")}>
            {t("pages:emailConfirmation.backToLogin")}
          </Button>
        </div>
      </CardWrapper>
    </div>
  );
}
